import CopyableId from "@/components/common/CopyableId";
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  CheckCircle2, XCircle, MessageCircleQuestion, Edit, Send, ClipboardCheck,
  UserCheck, PlayCircle, FileText, Clock, ShieldAlert, Trash2, RotateCcw,
} from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/layout/PageHeader";
import WorkflowBadge from "@/pages/onboarding/WorkflowBadge";
import { STATUS_LABELS, canCreateRequest, canMedicalReview, canOperationalReview, canAssignTeam, canConductVisit } from "@/pages/onboarding/constants";
import AttachmentsPanel from "@/pages/onboarding/AttachmentsPanel";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { FormSection, FormField, StickyActionBar } from "@/components/common/FormSection";
import { MultiSelect } from "@/components/common/MultiSelect";
import EmbeddedForms from "@/components/forms/EmbeddedForms";
import CareRecord from "@/components/clinical/CareRecord";
import LiveVisitTracker from "@/components/fleet/LiveVisitTracker";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useVisitTracking } from "@/hooks/useVisitTracking";

// Helper: a small field-display
function formatAddress(addr) {
  if (!addr) return null;
  if (typeof addr === "string") return addr;
  const parts = [addr.building_no, addr.line1 || addr.street, addr.district, addr.city]
    .filter(Boolean);
  const extra = [addr.landmark && `Near ${addr.landmark}`, addr.directions].filter(Boolean);
  return [parts.join(", "), ...extra].filter(Boolean).join(" · ") || null;
}


function Field({ label, value, full }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <div className="text-[11px] uppercase tracking-wider font-semibold text-[#6B7280]">{label}</div>
      <div className="text-sm text-[#1F2937] mt-0.5">{value || "—"}</div>
    </div>
  );
}

export default function RequestDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, hasPrivilege } = useAuth();
  const role = user?.role_code || "viewer";
  const canViewCareRecord = hasPrivilege?.("care_record.view");

  const [request, setRequest] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [comments, setComments] = useState([]);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");
  const [rejectionReasons, setRejectionReasons] = useState([]);
  const [infoReasons, setInfoReasons] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [addressable, setAddressable] = useState([]);
  const [addressIds, setAddressIds] = useState([]);
  const [placedForms, setPlacedForms] = useState([]);

  // Action modals
  const [approveOpen, setApproveOpen] = useState(false);
  const [approveStage, setApproveStage] = useState("medical"); // medical | operational
  const [decisionNotes, setDecisionNotes] = useState("");

  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectStage, setRejectStage] = useState("medical");
  const [rejectionReason, setRejectionReason] = useState("");
  const [rejectComments, setRejectComments] = useState("");

  const [infoOpen, setInfoOpen] = useState(false);
  const [requestedInfo, setRequestedInfo] = useState("");
  const [infoComments, setInfoComments] = useState("");

  const [readmitOpen, setReadmitOpen] = useState(false);
  const [readmitJustification, setReadmitJustification] = useState("");

  // Assessment + assignment + checklist sheets
  const [assessmentOpen, setAssessmentOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [checklistOpen, setChecklistOpen] = useState(false);

  // Live GPS tracking for onboarding home visit
  const tracking = useVisitTracking();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, t, c, a] = await Promise.all([
        api.get(`/onboarding/requests/${id}`),
        api.get(`/onboarding/requests/${id}/timeline`),
        api.get(`/onboarding/requests/${id}/comments`),
        api.get(`/onboarding/requests/${id}/audit`),
      ]);
      setRequest(r.data);
      setTimeline(t.data.items || []);
      setComments(c.data.items || []);
      setAudit(a.data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // Resume live GPS tracking on reload if the visit is still in progress.
  useEffect(() => {
    if (!request || tracking.isTracking) return;
    if (request.status !== "visit_in_progress") return;
    tracking.resumeIfActive(request.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request?.status]);

  useEffect(() => {
    api.get("/forms/placed", { params: { module: "onboarding" } })
      .then((r) => setPlacedForms(r.data.items)).catch(() => {});
  }, []);

  // Live comments: poll while the Comments tab is open so new comments from
  // other users appear in near real-time without a manual refresh.
  const refreshComments = useCallback(async () => {
    try {
      const c = await api.get(`/onboarding/requests/${id}/comments`);
      const items = c.data.items || [];
      setComments((prev) => {
        const prevLast = prev[prev.length - 1];
        const nextLast = items[items.length - 1];
        if (prev.length === items.length && prevLast?.id === nextLast?.id) return prev;
        return items;
      });
    } catch (e) { /* silent during polling */ }
  }, [id]);

  useEffect(() => {
    if (tab !== "comments") return;
    refreshComments();
    // Load the addressable users (4 workflow roles) once when the tab opens
    api.get(`/onboarding/requests/${id}/addressable`)
      .then((r) => setAddressable(r.data.items || [])).catch(() => {});
    const iv = setInterval(refreshComments, 5000);
    const onFocus = () => refreshComments();
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(iv); window.removeEventListener("focus", onFocus); };
  }, [tab, refreshComments, id]);

  useEffect(() => {
    async function loadLookups() {
      try {
        const cats = await api.get("/lookups/categories", { params: { page_size: 200 } });
        const rej = cats.data.items.find((c) => c.code === "rejection_reasons");
        const inf = cats.data.items.find((c) => c.code === "info_request_reasons");
        if (rej) {
          const v = await api.get("/lookups/values", { params: { category_id: rej.id } });
          setRejectionReasons(v.data.items);
        }
        if (inf) {
          const v = await api.get("/lookups/values", { params: { category_id: inf.id } });
          setInfoReasons(v.data.items);
        }
      } catch (_e) {}
    }
    loadLookups();
  }, []);

  if (loading || !request) {
    return <div className="p-6 text-sm text-[#6B7280]" data-testid="ob-detail-loading">Loading…</div>;
  }

  const status = request.status;
  const isCreator = user?.id === request.created_by;
  const canEdit = (status === "draft" || status === "information_requested") && (isCreator || role === "admin");
  const canSubmit = canEdit;
  const canMedDecide = status === "under_medical_review" && canMedicalReview(role);
  const canOpDecide = ["medical_approved", "under_operational_assessment"].includes(status) && canOperationalReview(role);
  const opAssessmentDone = assessmentComplete(request.initial_assessment);
  const canAssign = status === "operationally_approved" && canAssignTeam(role);
  const canStartVisit = status === "assigned" && canConductVisit(role);
  const canSaveChecklist = ["assigned", "visit_in_progress"].includes(status) && canConductVisit(role);
  const canCompleteVisit = status === "visit_in_progress" && canConductVisit(role);

  const readmitEligible = !!request.readmit_eligibility?.eligible && canCreateRequest(role);
  const canReadmitDecide = status === "readmit_pending" && canMedicalReview(role);
  const readmit = request.readmit || null;

  // ---------- action handlers ----------
  async function callAction(url, body = null, successMsg = "Done") {
    try {
      const res = body ? await api.post(url, body) : await api.post(url);
      setRequest(res.data);
      toast.success(successMsg);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  async function submitApprove() {
    const url = approveStage === "medical"
      ? `/onboarding/requests/${id}/medical-approve`
      : approveStage === "readmit"
      ? `/onboarding/requests/${id}/readmit-approve`
      : `/onboarding/requests/${id}/operational-approve`;
    if (!decisionNotes.trim()) { toast.error("Decision notes are required."); return; }
    const label = approveStage === "medical" ? "Medical approval recorded"
      : approveStage === "readmit" ? "Readmission approved" : "Operational approval recorded";
    await callAction(url, { decision_notes: decisionNotes }, label);
    setApproveOpen(false); setDecisionNotes("");
  }
  async function submitReject() {
    const url = rejectStage === "medical"
      ? `/onboarding/requests/${id}/medical-reject`
      : rejectStage === "readmit"
      ? `/onboarding/requests/${id}/readmit-reject`
      : `/onboarding/requests/${id}/operational-reject`;
    if (!rejectionReason) { toast.error("Rejection reason is required."); return; }
    if (!rejectComments.trim()) { toast.error("Comments are required."); return; }
    await callAction(url, { rejection_reason: rejectionReason, comments: rejectComments }, "Request rejected");
    setRejectOpen(false); setRejectionReason(""); setRejectComments("");
  }
  async function submitReadmit() {
    if (!readmitJustification.trim()) { toast.error("A justification is required."); return; }
    await callAction(`/onboarding/requests/${id}/readmit`, { justification: readmitJustification }, "Readmission requested — sent to physician");
    setReadmitOpen(false); setReadmitJustification("");
  }
  async function submitRequestInfo() {
    if (!requestedInfo.trim()) { toast.error("Requested information is required."); return; }
    await callAction(`/onboarding/requests/${id}/request-information`,
      { requested_information: requestedInfo, comments: infoComments }, "Information requested");
    setInfoOpen(false); setRequestedInfo(""); setInfoComments("");
  }

  async function addComment() {
    if (!newComment.trim()) return;
    try {
      await api.post(`/onboarding/requests/${id}/comments`, { body: newComment, addressed_to_ids: addressIds });
      setNewComment("");
      setAddressIds([]);
      await refreshComments();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  const ribbonActions = (
    <>
      {canEdit && (
        <Button variant="outline" className="border-[#E5E7EB]" onClick={() => navigate(`/onboarding/requests/${id}/edit`)} data-testid="ob-action-edit">
          <Edit className="w-4 h-4 mr-1.5" /> Edit
        </Button>
      )}
      {canSubmit && (
        <Button onClick={() => callAction(`/onboarding/requests/${id}/submit`, {}, "Submitted")} className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid="ob-action-submit">
          <Send className="w-4 h-4 mr-1.5" /> {status === "information_requested" ? "Resubmit" : "Submit"}
        </Button>
      )}
      {readmitEligible && (
        <Button variant="outline" className="border-[#D9770633] text-[#B45309] hover:bg-[#D9770614]" data-testid="ob-action-readmit" onClick={() => setReadmitOpen(true)}>
          <RotateCcw className="w-4 h-4 mr-1.5" /> Readmit
        </Button>
      )}
      {canReadmitDecide && (
        <>
          <Button variant="outline" className="border-[#EF444433] text-[#EF4444] hover:bg-[#EF444412]" data-testid="ob-action-readmit-reject" onClick={() => { setRejectStage("readmit"); setRejectOpen(true); }}>
            <XCircle className="w-4 h-4 mr-1.5" /> Reject Readmission
          </Button>
          <Button className="bg-[#10B981] hover:bg-[#059669] text-white" data-testid="ob-action-readmit-approve" onClick={() => { setApproveStage("readmit"); setApproveOpen(true); }}>
            <CheckCircle2 className="w-4 h-4 mr-1.5" /> Approve Readmission
          </Button>
        </>
      )}
      {canMedDecide && (
        <>
          <Button variant="outline" className="border-[#F59E0B33] text-[#92400E] hover:bg-[#F59E0B14]" data-testid="ob-action-request-info" onClick={() => setInfoOpen(true)}>
            <MessageCircleQuestion className="w-4 h-4 mr-1.5" /> Request Information
          </Button>
          <Button variant="outline" className="border-[#EF444433] text-[#EF4444] hover:bg-[#EF444412]" data-testid="ob-action-medical-reject" onClick={() => { setRejectStage("medical"); setRejectOpen(true); }}>
            <XCircle className="w-4 h-4 mr-1.5" /> Reject
          </Button>
          <Button className="bg-[#10B981] hover:bg-[#059669] text-white" data-testid="ob-action-medical-approve" onClick={() => { setApproveStage("medical"); setApproveOpen(true); }}>
            <CheckCircle2 className="w-4 h-4 mr-1.5" /> Medical Approve
          </Button>
        </>
      )}
      {canOpDecide && (
        <>
          <Button variant="outline" className="border-[#E5E7EB]" data-testid="ob-action-assessment" onClick={() => setAssessmentOpen(true)}>
            <ClipboardCheck className="w-4 h-4 mr-1.5" /> Initial Assessment{!opAssessmentDone && <span className="ml-1.5 w-2 h-2 rounded-full bg-[#F59E0B] inline-block" />}
          </Button>
          <Button variant="outline" disabled={!opAssessmentDone} title={!opAssessmentDone ? "Complete the initial assessment first" : undefined} className="border-[#EF444433] text-[#EF4444] hover:bg-[#EF444412] disabled:opacity-40" data-testid="ob-action-operational-reject" onClick={() => { setRejectStage("operational"); setRejectOpen(true); }}>
            <XCircle className="w-4 h-4 mr-1.5" /> Reject
          </Button>
          <Button disabled={!opAssessmentDone} title={!opAssessmentDone ? "Complete the initial assessment first" : undefined} className="bg-[#10B981] hover:bg-[#059669] text-white disabled:opacity-40" data-testid="ob-action-operational-approve" onClick={() => { setApproveStage("operational"); setApproveOpen(true); }}>
            <CheckCircle2 className="w-4 h-4 mr-1.5" /> Operationally Approve
          </Button>
        </>
      )}
      {canAssign && (
        <Button className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid="ob-action-assign" onClick={() => setAssignOpen(true)}>
          <UserCheck className="w-4 h-4 mr-1.5" /> Assign Home Care Team
        </Button>
      )}
      {canStartVisit && (
        <Button className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid="ob-action-start-visit"
          onClick={async () => {
            try {
              await tracking.startTracking({
                visitId: id, kind: "onboarding",
                label: request.ref_number,
              });
            } catch (e) {
              toast.error(e.message || "Location access is required to start the visit.");
              return;
            }
            await callAction(`/onboarding/requests/${id}/start-visit`, null, "Visit started");
          }}>
          <PlayCircle className="w-4 h-4 mr-1.5" /> Start Visit
        </Button>
      )}
      {canSaveChecklist && (
        <Button variant="outline" className="border-[#E5E7EB]" data-testid="ob-action-checklist" onClick={() => setChecklistOpen(true)}>
          <ClipboardCheck className="w-4 h-4 mr-1.5" /> Visit Checklist
        </Button>
      )}
      {canCompleteVisit && (
        <Button className="bg-[#10B981] hover:bg-[#059669] text-white" data-testid="ob-action-complete-visit"
          onClick={async () => {
            try { await tracking.endTracking(); } catch { /* non-fatal */ }
            await callAction(`/onboarding/requests/${id}/complete-visit`, null, "Visit completed");
          }}>
          <CheckCircle2 className="w-4 h-4 mr-1.5" /> Complete Visit
        </Button>
      )}
    </>
  );

  return (
    <div data-testid="ob-detail-page">
      {/* Live GPS tracking overlay for the assigned provider during a home visit */}
      <LiveVisitTracker
        tracking={tracking}
        onEndVisit={async () => {
          try {
            await api.post(`/onboarding/requests/${id}/complete-visit`);
            toast.success("Visit completed");
            load();
          } catch (e) { toast.error(formatApiError(e)); }
        }}
      />
      <PageHeader
        title={
          <span className="flex items-center gap-3">
            <span>{request.ref_number}</span>
            <WorkflowBadge status={status} testId="ob-detail-status" />
          </span>
        }
        subtitle={`${[request.patient?.first_name, request.patient?.middle_name, request.patient?.last_name].filter(Boolean).join(" ")} · ${request.patient?.mrn} · ${request.clinical?.primary_diagnosis || "—"}`}
        actions={<div className="flex flex-wrap items-center gap-2">{ribbonActions}</div>}
      />

      {request.status === "information_requested" && (
        <div className="mb-4 p-4 rounded-lg bg-[#F59E0B14] border border-[#F59E0B33] text-[#92400E]" data-testid="ob-info-banner">
          <div className="font-semibold mb-1">Information requested by physician</div>
          <div className="text-sm">{request.information_requested}</div>
          {request.information_request_notes && <div className="text-xs mt-1 opacity-80">{request.information_request_notes}</div>}
        </div>
      )}
      {request.status === "rejected" && (
        <div className="mb-4 p-4 rounded-lg bg-[#EF444412] border border-[#EF444433] text-[#B91C1C]" data-testid="ob-rejected-banner">
          <div className="font-semibold mb-1">Request rejected</div>
          <div className="text-sm">Reason: {request.rejection_reason} {request.rejection_notes ? `— ${request.rejection_notes}` : ""}</div>
        </div>
      )}
      {readmitEligible && (
        <div className="mb-4 p-4 rounded-lg bg-[#D9770614] border border-[#D9770633] text-[#92400E]" data-testid="ob-readmit-eligible-banner">
          <div className="font-semibold mb-1 flex items-center gap-1.5"><RotateCcw className="w-4 h-4" /> Eligible for fast-track readmission</div>
          <div className="text-sm">This patient was dispositioned {request.readmit_eligibility.hours_since}h ago ({request.readmit_eligibility.disposition_label || "—"}). You can request a <strong>Readmit</strong> with justification (≈{request.readmit_eligibility.hours_remaining}h left in the 48-hour window) — it will be sent to an H@H physician for approval.</div>
        </div>
      )}
      {readmit && status === "readmit_pending" && (
        <div className="mb-4 p-4 rounded-lg bg-[#D9770614] border border-[#D9770633] text-[#92400E]" data-testid="ob-readmit-pending-banner">
          <div className="font-semibold mb-1 flex items-center gap-1.5"><RotateCcw className="w-4 h-4" /> Readmission pending physician approval</div>
          <div className="text-sm"><span className="font-medium">Justification:</span> {readmit.justification}</div>
          <div className="text-xs mt-1 opacity-80">Requested by {readmit.requested_by_name} · prior disposition: {readmit.prior_disposition_label || "—"} ({readmit.hours_since_disposition}h before request)</div>
        </div>
      )}
      {readmit && readmit.status === "rejected" && status !== "readmit_pending" && (
        <div className="mb-4 p-4 rounded-lg bg-[#EF444412] border border-[#EF444433] text-[#B91C1C]" data-testid="ob-readmit-rejected-banner">
          <div className="font-semibold mb-1">Readmission rejected — proceed via standard medical review</div>
          <div className="text-sm">Reason: {readmit.rejection_reason} {readmit.rejection_notes ? `— ${readmit.rejection_notes}` : ""}</div>
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-[#F5F7FA]">
          <TabsTrigger value="overview" data-testid="ob-tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="timeline" data-testid="ob-tab-timeline">Timeline</TabsTrigger>
          <TabsTrigger value="attachments" data-testid="ob-tab-attachments">Attachments</TabsTrigger>
          <TabsTrigger value="comments" data-testid="ob-tab-comments">Comments</TabsTrigger>
          <TabsTrigger value="audit" data-testid="ob-tab-audit">Audit History</TabsTrigger>
          {request.initial_assessment && <TabsTrigger value="assessment" data-testid="ob-tab-assessment">Assessment</TabsTrigger>}
          {request.visit_checklist && <TabsTrigger value="visit" data-testid="ob-tab-visit">Visit Findings</TabsTrigger>}
          {placedForms.length > 0 && <TabsTrigger value="forms" data-testid="ob-tab-forms">Forms</TabsTrigger>}
          {canViewCareRecord && <TabsTrigger value="care-record" data-testid="ob-tab-care-record">Care Record</TabsTrigger>}
        </TabsList>

        <TabsContent value="overview" className="mt-4 space-y-4">
          <div className="hah-card p-5">
            <div className="hah-section-title mb-1">Patient</div>
            <h3 className="text-base font-semibold text-[#1F2937] mb-4">{[request.patient?.first_name, request.patient?.middle_name, request.patient?.last_name].filter(Boolean).join(" ")}</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
              <Field label="MRN" value={<CopyableId value={request.patient?.mrn} kind="MRN" />} />
              <Field label="Identity type" value={request.patient?.identity_type} />
              <Field label="National ID" value={<CopyableId value={request.patient?.national_id} kind="Identity number" />} />
              <Field label="DOB" value={request.patient?.dob} />
              <Field label="Gender" value={request.patient?.gender} />
              <Field label="Nationality" value={request.patient?.nationality} />
              <Field label="Mobile" value={[request.patient?.dial_code, request.patient?.phone].filter(Boolean).join(" ") || null} />
              <Field label="Email" value={request.patient?.email} />
              <Field label="City" value={request.patient?.city} />
              <Field label="Address" value={formatAddress(request.patient?.address)} full />
              {request.patient?.address?.short_address && (
                <Field label="National Address (Short Address)" value={request.patient.address.short_address} />
              )}
            </div>
          </div>
          <div className="hah-card p-5">
            <div className="hah-section-title mb-1">Referral</div>
            <h3 className="text-base font-semibold text-[#1F2937] mb-4">Referral information</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <Field label="Source" value={request.referral?.source} />
              <Field label="Department" value={request.referral?.department} />
              <Field label="Referral date" value={request.referral?.referral_date} />
              <Field label="Referring physician" value={request.referral?.referring_physician} full />
            </div>
          </div>
          <div className="hah-card p-5">
            <div className="hah-section-title mb-1">Clinical</div>
            <h3 className="text-base font-semibold text-[#1F2937] mb-4">Clinical information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <Field label="Primary diagnosis" value={request.clinical?.primary_diagnosis} />
              <Field label="Secondary diagnosis" value={request.clinical?.secondary_diagnosis} />
              <Field label="Allergies" value={request.clinical?.allergies} />
              <Field label="Medications" value={request.clinical?.current_medications} />
              <Field label="Clinical summary" value={request.clinical?.clinical_summary} full />
              <Field label="Clinical notes" value={request.clinical?.clinical_notes} full />
            </div>
          </div>
          <div className="hah-card p-5">
            <div className="hah-section-title mb-1">Service</div>
            <h3 className="text-base font-semibold text-[#1F2937] mb-4">Service request</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <Field label="Requested service" value={request.service?.requested_service} />
              <Field label="Priority" value={request.service?.priority} />
              <Field label="Expected start" value={request.service?.expected_start_date} />
            </div>
          </div>
          {request.assignment && (
            <div className="hah-card p-5" data-testid="ob-assignment-display">
              <div className="hah-section-title mb-1">Assignment</div>
              <h3 className="text-base font-semibold text-[#1F2937] mb-4">Home care team</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <Field
                  label="Nurses"
                  value={(request.assignment.nurses || []).map((n) => n.name).join(", ") || request.assignment.team_lead_id}
                />
                <Field
                  label="H@H Technicians"
                  value={(request.assignment.technicians || []).map((t) => t.name).join(", ")}
                />
                <Field label="Visit date" value={request.assignment.visit_date} />
                <Field label="Visit time" value={request.assignment.visit_time} />
                <Field label="Priority" value={request.assignment.priority} />
                <Field label="Notes" value={request.assignment.notes} full />
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="timeline" className="mt-4">
          <div className="hah-card p-5" data-testid="ob-timeline">
            <ol className="relative border-l border-[#E5E7EB] ml-3 space-y-5">
              {timeline.length === 0 && <li className="ml-4 text-sm text-[#6B7280]">No events yet.</li>}
              {timeline.map((e) => (
                <li key={e.id} className="ml-4 relative">
                  <span className="absolute -left-[24px] top-1 w-3 h-3 rounded-full bg-[#0B7E8C] ring-4 ring-white" />
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-[#1F2937]">{e.title}</span>
                    <span className="text-xs text-[#6B7280]">{new Date(e.created_at).toLocaleString()}</span>
                  </div>
                  {e.description && <div className="text-sm text-[#6B7280] mt-1">{e.description}</div>}
                  <div className="text-xs text-[#94A3B8] mt-1">
                    {e.actor_name || e.actor_email || "system"} {e.actor_role ? `· ${e.actor_role.replace(/_/g, " ")}` : ""}
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </TabsContent>

        <TabsContent value="attachments" className="mt-4">
          <AttachmentsPanel requestId={id} />
        </TabsContent>

        <TabsContent value="comments" className="mt-4">
          <div className="hah-card p-5" data-testid="ob-comments">
            <h3 className="text-base font-semibold text-[#1F2937] mb-3 flex items-center gap-2">
              Comments
              <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-[#10B981]" data-testid="ob-comments-live">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#10B981] opacity-60"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-[#10B981]"></span>
                </span>
                Live
              </span>
            </h3>
            <div className="space-y-3 mb-4">
              {comments.length === 0 && <div className="text-sm text-[#6B7280]">No comments yet.</div>}
              {comments.map((c) => (
                <div key={c.id} className="flex items-start gap-3 p-3 rounded-lg border border-[#E5E7EB] bg-[#F5F7FA]/50">
                  <div className="w-8 h-8 rounded-full bg-[#EAF8FA] text-[#0B7E8C] flex items-center justify-center text-xs font-semibold">
                    {(c.author_email || "?").slice(0, 2).toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-[#1F2937]">{c.author_name || c.author_email}</span>
                      {c.author_role && <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#EAF8FA] text-[#0B7E8C]">{c.author_role.replace(/_/g, " ")}</span>}
                      <span className="text-xs text-[#6B7280]">{new Date(c.created_at).toLocaleString()}</span>
                    </div>
                    {(c.addressed_to?.length > 0) && (
                      <div className="flex items-center gap-1.5 flex-wrap mt-1" data-testid={`ob-comment-addressed-${c.id}`}>
                        <span className="text-[11px] text-[#6B7280]">To:</span>
                        {c.addressed_to.map((a) => (
                          <span key={a.user_id} className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#F0FDF4] text-[#065F46] border border-[#10B981]/30">
                            {a.name} · {a.role_label || (a.role || "").replace(/_/g, " ")}
                          </span>
                        ))}
                      </div>
                    )}
                    <div className="text-sm text-[#1F2937] mt-1 whitespace-pre-wrap">{c.body}</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="space-y-2">
              <div className="max-w-md">
                <label className="text-xs font-medium text-[#475569] mb-1 block">Address to (optional)</label>
                <MultiSelect
                  testid="ob-comment-address"
                  placeholder="Address colleagues…"
                  emptyText="No workflow users available"
                  showPrimaryBadge={false}
                  options={addressable.map((a) => ({ value: a.user_id, label: a.name, sublabel: a.role_label }))}
                  values={addressIds}
                  onChange={setAddressIds}
                />
              </div>
              <div className="flex items-end gap-2">
                <Textarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Add a comment…"
                  rows={2}
                  data-testid="ob-comment-input"
                  className="flex-1"
                />
                <Button onClick={addComment} disabled={!newComment.trim()} className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid="ob-comment-submit">
                  Post
                </Button>
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="audit" className="mt-4">
          <div className="hah-card p-5">
            <h3 className="text-base font-semibold text-[#1F2937] mb-3">Audit history</h3>
            {audit.length === 0 ? (
              <div className="text-sm text-[#6B7280]">No audit events.</div>
            ) : (
              <div className="overflow-x-auto"><table className="w-full text-sm">
                <thead className="bg-[#F5F7FA] text-[11px] uppercase tracking-wider text-[#6B7280]">
                  <tr>
                    <th className="text-left px-3 py-2">When</th>
                    <th className="text-left px-3 py-2">Action</th>
                    <th className="text-left px-3 py-2">By</th>
                    <th className="text-left px-3 py-2">IP</th>
                  </tr>
                </thead>
                <tbody>
                  {audit.map((a) => (
                    <tr key={a.id} className="border-t border-[#E5E7EB]">
                      <td className="px-3 py-2 text-xs text-[#6B7280]">{new Date(a.created_at).toLocaleString()}</td>
                      <td className="px-3 py-2"><WorkflowBadge status={a.action.toLowerCase()} /></td>
                      <td className="px-3 py-2 text-[#1F2937]">{a.user_email || "system"}</td>
                      <td className="px-3 py-2 text-xs text-[#6B7280] font-mono">{a.ip || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table></div>
            )}
          </div>
        </TabsContent>

        {request.initial_assessment && (
          <TabsContent value="assessment" className="mt-4">
            <div className="hah-card p-5">
              <h3 className="text-base font-semibold text-[#1F2937] mb-4">Initial assessment</h3>
              {Object.entries({
                "Patient Understanding": request.initial_assessment.patient_understanding,
                "Companion": request.initial_assessment.caregiver,
                "Home Environment": request.initial_assessment.home_environment,
                "Accessibility": request.initial_assessment.accessibility,
                "Connectivity": request.initial_assessment.connectivity,
                "Consent Readiness": request.initial_assessment.consent_readiness,
              }).map(([section, body]) => (
                <div key={section} className="mb-4 border border-[#E5E7EB] rounded-lg p-4">
                  <div className="font-medium text-[#1F2937] mb-2">{section}</div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                    {Object.entries((body || {}).answers || {}).map(([k, v]) => (
                      <Field key={k} label={k.replace(/_/g, " ")} value={typeof v === "boolean" ? (v ? "Yes" : "No") : String(v)} />
                    ))}
                  </div>
                  {(body || {}).notes && <div className="text-xs text-[#6B7280] mt-2">{body.notes}</div>}
                </div>
              ))}
            </div>
          </TabsContent>
        )}

        {request.visit_checklist && (
          <TabsContent value="visit" className="mt-4">
            <div className="hah-card p-5">
              <h3 className="text-base font-semibold text-[#1F2937] mb-4">Visit findings</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                {Object.entries(request.visit_checklist).filter(([k]) => typeof request.visit_checklist[k] === "boolean").map(([k, v]) => (
                  <div key={k} className="flex items-center gap-2">
                    {v ? <CheckCircle2 className="w-4 h-4 text-[#10B981]" /> : <XCircle className="w-4 h-4 text-[#EF4444]" />}
                    <span className="capitalize text-[#1F2937]">{k.replace(/_/g, " ")}</span>
                  </div>
                ))}
              </div>
              {request.visit_checklist.additional_findings && <Field label="Additional findings" value={request.visit_checklist.additional_findings} full />}
              {request.visit_checklist.visit_notes && <Field label="Visit notes" value={request.visit_checklist.visit_notes} full />}
            </div>
          </TabsContent>
        )}

        {placedForms.length > 0 && (
          <TabsContent value="forms" className="mt-4">
            <EmbeddedForms module="onboarding" entityId={request.id} entityLabel={request.ref_number} forms={placedForms} />
          </TabsContent>
        )}

        {canViewCareRecord && (
          <TabsContent value="care-record" className="mt-4">
            <CareRecord requestId={request.id} refNumber={request.ref_number} />
          </TabsContent>
        )}
      </Tabs>

      {/* --- Approve dialog --- */}
      <Dialog open={approveOpen} onOpenChange={setApproveOpen}>
        <DialogContent data-testid="ob-approve-dialog">
          <DialogHeader>
            <DialogTitle>{approveStage === "medical" ? "Medical approval" : approveStage === "readmit" ? "Approve readmission" : "Operational approval"}</DialogTitle>
            <DialogDescription>
              {approveStage === "medical"
                ? "Approving will forward this request to the H@H Care Coordinator for operational assessment."
                : approveStage === "readmit"
                ? "Approving the readmission forwards this request to the H@H Care Coordinator for operational assessment (normal visit cycle continues)."
                : "Approving will mark this request ready for home care team assignment."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-sm font-medium text-[#1F2937]">Decision notes <span className="text-[#EF4444]">*</span></label>
            <Textarea rows={4} value={decisionNotes} onChange={(e) => setDecisionNotes(e.target.value)} data-testid="ob-approve-notes" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApproveOpen(false)} className="border-[#E5E7EB]">Cancel</Button>
            <Button onClick={submitApprove} className="bg-[#10B981] hover:bg-[#059669] text-white" data-testid="ob-approve-confirm">
              <CheckCircle2 className="w-4 h-4 mr-1.5" /> Confirm approval
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* --- Reject dialog --- */}
      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent data-testid="ob-reject-dialog">
          <DialogHeader>
            <DialogTitle>{rejectStage === "medical" ? "Medical rejection" : rejectStage === "readmit" ? "Reject readmission" : "Operational rejection"}</DialogTitle>
            <DialogDescription>{rejectStage === "readmit" ? "The request returns to draft so the coordinator can submit it for standard medical review." : "This will close the workflow. The hospital coordinator will be notified."}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium text-[#1F2937]">Rejection reason <span className="text-[#EF4444]">*</span></label>
              <Select value={rejectionReason} onValueChange={setRejectionReason}>
                <SelectTrigger data-testid="ob-reject-reason"><SelectValue placeholder="Select a reason" /></SelectTrigger>
                <SelectContent>
                  {rejectionReasons.map((r) => <SelectItem key={r.id} value={r.value}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-[#1F2937]">Comments <span className="text-[#EF4444]">*</span></label>
              <Textarea rows={4} value={rejectComments} onChange={(e) => setRejectComments(e.target.value)} data-testid="ob-reject-comments" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectOpen(false)} className="border-[#E5E7EB]">Cancel</Button>
            <Button onClick={submitReject} className="bg-[#EF4444] hover:bg-[#DC2626] text-white" data-testid="ob-reject-confirm">
              <XCircle className="w-4 h-4 mr-1.5" /> Confirm rejection
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* --- Request information dialog --- */}
      <Dialog open={infoOpen} onOpenChange={setInfoOpen}>
        <DialogContent data-testid="ob-info-dialog">
          <DialogHeader>
            <DialogTitle>Request information</DialogTitle>
            <DialogDescription>The request will go back to the hospital coordinator for additional details.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium text-[#1F2937]">What information is needed? <span className="text-[#EF4444]">*</span></label>
              <Textarea rows={3} value={requestedInfo} onChange={(e) => setRequestedInfo(e.target.value)} data-testid="ob-info-requested" />
            </div>
            <div>
              <label className="text-sm font-medium text-[#1F2937]">Comments</label>
              <Textarea rows={2} value={infoComments} onChange={(e) => setInfoComments(e.target.value)} data-testid="ob-info-comments" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInfoOpen(false)} className="border-[#E5E7EB]">Cancel</Button>
            <Button onClick={submitRequestInfo} className="bg-[#F59E0B] hover:bg-[#D97706] text-white" data-testid="ob-info-confirm">
              <MessageCircleQuestion className="w-4 h-4 mr-1.5" /> Send request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* --- Readmission request dialog --- */}
      <Dialog open={readmitOpen} onOpenChange={setReadmitOpen}>
        <DialogContent data-testid="ob-readmit-dialog">
          <DialogHeader>
            <DialogTitle>Request readmission</DialogTitle>
            <DialogDescription>
              This patient was dispositioned {request.readmit_eligibility?.hours_since}h ago ({request.readmit_eligibility?.disposition_label || "—"}). Provide a justification — it will be sent to an H@H physician for approval before resuming the care cycle.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-sm font-medium text-[#1F2937]">Justification <span className="text-[#EF4444]">*</span></label>
            <Textarea rows={4} value={readmitJustification} onChange={(e) => setReadmitJustification(e.target.value)} placeholder="Clinical reason for readmitting within 48 hours…" data-testid="ob-readmit-justification" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReadmitOpen(false)} className="border-[#E5E7EB]">Cancel</Button>
            <Button onClick={submitReadmit} className="bg-[#D97706] hover:bg-[#B45309] text-white" data-testid="ob-readmit-confirm">
              <RotateCcw className="w-4 h-4 mr-1.5" /> Submit for approval
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* --- Assessment sheet --- */}
      <InitialAssessmentSheet
        open={assessmentOpen}
        onOpenChange={setAssessmentOpen}
        requestId={id}
        initial={request.initial_assessment}
        onSaved={(r) => { setRequest(r); load(); }}
      />

      {/* --- Assign sheet --- */}
      <AssignTeamSheet
        open={assignOpen}
        onOpenChange={setAssignOpen}
        requestId={id}
        onSaved={(r) => { setRequest(r); load(); }}
      />

      {/* --- Visit checklist sheet --- */}
      <VisitChecklistSheet
        open={checklistOpen}
        onOpenChange={setChecklistOpen}
        requestId={id}
        initial={request.visit_checklist}
        canComplete={canCompleteVisit}
        onSaved={(r) => { setRequest(r); load(); }}
      />
    </div>
  );
}


// --------------- Initial Assessment Sheet ---------------
const ASSESSMENT_QUESTIONS = {
  patient_understanding: {
    title: "Patient Understanding",
    items: [
      ["explained", "Program has been explained"],
      ["understands_program", "Patient understands Hospital @ Home"],
      ["willing_to_participate", "Patient willing to participate"],
      ["understands_escalation", "Patient understands escalation process"],
    ],
  },
  caregiver: {
    title: "Companion Assessment",
    items: [
      ["available", "Companion available"],
      ["willing", "Companion willing"],
    ],
    textFields: [
      ["name", "Companion name"], ["relationship", "Relationship"], ["contact", "Contact number"],
    ],
  },
  home_environment: {
    title: "Home Environment",
    items: [
      ["safe", "Safe environment"], ["electricity", "Electricity available"], ["water", "Running water available"],
    ],
  },
  accessibility: {
    title: "Accessibility",
    items: [
      ["accessible", "Accessible residence"], ["bedroom", "Suitable bedroom"], ["bathroom", "Suitable bathroom"],
    ],
  },
  connectivity: {
    title: "Connectivity",
    items: [
      ["mobile", "Mobile coverage"], ["internet", "Internet available"], ["smartphone", "Smartphone available"],
    ],
  },
  consent_readiness: {
    title: "Consent Readiness",
    items: [["willing_to_sign", "Patient willing to sign consent"]],
  },
};

// Every yes/no question must be answered before an operational decision (notes/text optional).
const ASSESSMENT_REQUIRED = Object.fromEntries(
  Object.entries(ASSESSMENT_QUESTIONS).map(([sec, def]) => [sec, (def.items || []).map(([k]) => k)])
);
function assessmentComplete(ia) {
  if (!ia) return false;
  return Object.entries(ASSESSMENT_REQUIRED).every(([sec, keys]) =>
    keys.every((k) => (ia[sec]?.answers || {})[k] !== undefined && (ia[sec]?.answers || {})[k] !== null)
  );
}
// Flat field-name -> section maps for auto-fill matching.
const ASSESS_ITEM_SECTION = {};
const ASSESS_TEXT_SECTION = {};
Object.entries(ASSESSMENT_QUESTIONS).forEach(([sec, def]) => {
  (def.items || []).forEach(([k]) => { ASSESS_ITEM_SECTION[k] = sec; });
  (def.textFields || []).forEach(([k]) => { ASSESS_TEXT_SECTION[k] = sec; });
});
// Accepted dynamic-form field names per assessment key (exact key always matches;
// common descriptive variants are aliased so real-world forms auto-fill too).
const ASSESS_ALIASES = {
  explained: ["program_explained", "explained_to_patient"],
  understands_program: ["understands", "program_understood"],
  willing_to_participate: ["participate", "agrees_to_participate"],
  understands_escalation: ["escalation_understood", "knows_escalation"],
  available: ["caregiver_available", "caregiver_present", "has_caregiver"],
  willing: ["caregiver_willing", "caregiver_consent"],
  name: ["caregiver_name"],
  relationship: ["caregiver_relationship"],
  contact: ["caregiver_contact", "caregiver_phone"],
  safe: ["safe_environment", "home_safe", "environment_safe", "suitability_outcome", "home_suitable"],
  electricity: ["electricity_available", "power", "power_supply", "power_available"],
  water: ["running_water", "water_available", "water_supply", "clean_water"],
  accessible: ["home_accessible", "access", "wheelchair_accessible"],
  bedroom: ["dedicated_bedroom", "bedroom_available", "private_bedroom"],
  bathroom: ["bathroom_available", "private_bathroom", "accessible_bathroom"],
  mobile: ["mobile_signal", "mobile_coverage", "cellular"],
  internet: ["internet_access", "wifi", "wifi_available", "broadband"],
  smartphone: ["has_smartphone", "smartphone_available"],
  willing_to_sign: ["consent_willing", "consent", "willing_consent"],
};
// Reverse lookup: form field name -> { key, sec, type }.
const FIELD_TO_ASSESS = {};
Object.keys({ ...ASSESS_ITEM_SECTION, ...ASSESS_TEXT_SECTION }).forEach((key) => {
  const sec = ASSESS_ITEM_SECTION[key] || ASSESS_TEXT_SECTION[key];
  const type = ASSESS_ITEM_SECTION[key] ? "bool" : "text";
  [key, ...(ASSESS_ALIASES[key] || [])].forEach((n) => { FIELD_TO_ASSESS[n.toLowerCase()] = { key, sec, type }; });
});
function toBoolAnswer(v) {
  if (typeof v === "boolean") return v;
  if (typeof v === "number") return v !== 0;
  if (typeof v === "string") {
    const s = v.trim().toLowerCase();
    if (["yes", "true", "1", "y", "present", "available", "suitable"].includes(s)) return true;
    if (["no", "false", "0", "n", "absent", "unavailable", "not suitable"].includes(s)) return false;
  }
  return undefined;
}

function InitialAssessmentSheet({ open, onOpenChange, requestId, initial, onSaved }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [prefillInfo, setPrefillInfo] = useState(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      const base = {};
      Object.keys(ASSESSMENT_QUESTIONS).forEach((sec) => {
        base[sec] = { answers: { ...((initial && initial[sec] && initial[sec].answers) || {}) }, notes: (initial && initial[sec] && initial[sec].notes) || "" };
      });
      let info = null;
      try {
        const res = await api.get(`/onboarding/requests/${requestId}/assessment-prefill`);
        const data = res.data?.data || {};
        const filled = [];
        Object.entries(data).forEach(([fname, val]) => {
          const m = FIELD_TO_ASSESS[String(fname).toLowerCase()];
          if (!m) return;
          if (m.type === "bool" && base[m.sec].answers[m.key] === undefined) {
            const b = toBoolAnswer(val);
            if (b !== undefined) { base[m.sec].answers[m.key] = b; filled.push(m.key); }
          } else if (m.type === "text" && !base[m.sec].answers[m.key]) {
            if (val !== null && val !== undefined && val !== "") { base[m.sec].answers[m.key] = String(val); filled.push(m.key); }
          }
        });
        if (res.data?.submission_id && filled.length) info = { form_name: res.data.form_name, count: filled.length };
      } catch (_e) { /* no prefill available */ }
      if (!cancelled) { setForm(base); setPrefillInfo(info); }
    })();
    return () => { cancelled = true; };
  }, [open, initial, requestId]);

  function setAns(sec, key, val) {
    setForm((f) => ({ ...f, [sec]: { ...(f[sec] || {}), answers: { ...((f[sec] || {}).answers || {}), [key]: val } } }));
  }
  function setNotes(sec, val) {
    setForm((f) => ({ ...f, [sec]: { ...(f[sec] || {}), notes: val } }));
  }

  async function save() {
    setSaving(true);
    try {
      const res = await api.post(`/onboarding/requests/${requestId}/initial-assessment`, form);
      onSaved && onSaved(res.data);
      toast.success("Assessment saved");
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-[700px] overflow-y-auto p-0 bg-[#FAFBFC]">
        <SheetHeader className="px-6 py-5 border-b border-[#E5E7EB] bg-white">
          <SheetTitle>Initial Assessment</SheetTitle>
          <SheetDescription>Assess patient suitability before assigning the home care team. Decision is fully manual — no scoring.</SheetDescription>
        </SheetHeader>
        <div className="p-6 space-y-4">
          {prefillInfo && (
            <div className="hah-card p-3 border-[#0B7E8C33] bg-[#EAF8FA] text-sm text-[#0B5963] flex items-center gap-2" data-testid="ob-assess-prefill-hint">
              <ClipboardCheck className="w-4 h-4 shrink-0" />
              <span>Auto-filled {prefillInfo.count} field{prefillInfo.count === 1 ? "" : "s"} from "{prefillInfo.form_name}". Review and edit as needed.</span>
            </div>
          )}
          {Object.entries(ASSESSMENT_QUESTIONS).map(([sec, def]) => (
            <FormSection key={sec} title={def.title} testId={`ob-assess-${sec}`}>
              {def.items.map(([k, label]) => (
                <FormField key={k} label={label}>
                  <Select
                    value={form[sec]?.answers?.[k] === undefined ? "" : (form[sec].answers[k] ? "yes" : "no")}
                    onValueChange={(v) => setAns(sec, k, v === "yes")}
                  >
                    <SelectTrigger data-testid={`ob-assess-${sec}-${k}`}><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="yes">Yes</SelectItem>
                      <SelectItem value="no">No</SelectItem>
                    </SelectContent>
                  </Select>
                </FormField>
              ))}
              {(def.textFields || []).map(([k, label]) => (
                <FormField key={k} label={label}>
                  <Input
                    value={form[sec]?.answers?.[k] || ""}
                    onChange={(e) => setAns(sec, k, e.target.value)}
                    data-testid={`ob-assess-${sec}-${k}`}
                  />
                </FormField>
              ))}
              <FormField label="Additional notes" full>
                <Textarea
                  rows={2}
                  value={form[sec]?.notes || ""}
                  onChange={(e) => setNotes(sec, e.target.value)}
                  data-testid={`ob-assess-${sec}-notes`}
                />
              </FormField>
            </FormSection>
          ))}
        </div>
        <StickyActionBar>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-[#E5E7EB]">Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid="ob-assess-save">
            {saving ? "Saving…" : "Save assessment"}
          </Button>
        </StickyActionBar>
      </SheetContent>
    </Sheet>
  );
}


// --------------- Assign Team Sheet ---------------
function ClinicianPicker({ title, people, selectedIds, onToggle, testId }) {
  return (
    <div data-testid={testId}>
      <div className="text-[11px] uppercase tracking-wider font-semibold text-[#6B7280] mb-2">{title}</div>
      {people.length === 0 ? (
        <div className="text-sm text-[#9CA3AF] italic">No active staff found for this role.</div>
      ) : (
        <div className="space-y-2">
          {people.map((p) => {
            const selected = selectedIds.includes(p.id);
            const busy = (p.booked_count || 0) > 0;
            return (
              <button
                type="button"
                key={p.id}
                data-testid={`clinician-${p.id}`}
                onClick={() => onToggle(p.id)}
                className={`w-full text-left rounded-lg border px-3 py-2.5 transition-colors ${
                  selected ? "border-[#0B7E8C] bg-[#0B7E8C]/5 ring-1 ring-[#0B7E8C]" : "border-[#E5E7EB] hover:border-[#0B7E8C]/50 bg-white"
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className={`w-4 h-4 rounded border flex items-center justify-center text-white ${selected ? "bg-[#0B7E8C] border-[#0B7E8C]" : "border-[#D1D5DB]"}`}>
                      {selected && <CheckCircle2 className="w-3.5 h-3.5" />}
                    </span>
                    <span className="text-sm font-medium text-[#1F2937]">{p.name}</span>
                  </div>
                  {busy ? (
                    <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
                      <ShieldAlert className="w-3 h-3" /> {p.booked_count} on this day
                    </span>
                  ) : (
                    <span className="text-[11px] font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">Available</span>
                  )}
                </div>
                {busy && (
                  <div className="mt-1.5 pl-6 text-[11px] text-amber-700/90 space-y-0.5">
                    {p.bookings.map((b, i) => (
                      <div key={i}>• {b.type === "fleet" ? "Fleet visit" : "Onboarding"} {b.label ? `(${b.label})` : ""} {b.time ? `— ${b.time}` : ""}</div>
                    ))}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function AssignTeamSheet({ open, onOpenChange, requestId, onSaved }) {
  const [nurses, setNurses] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [form, setForm] = useState({ nurse_ids: [], technician_ids: [], visit_date: "", visit_time: "", priority: "routine", notes: "" });
  const [saving, setSaving] = useState(false);
  const [loadingAvail, setLoadingAvail] = useState(false);

  // Load availability whenever the sheet opens or the visit date changes
  useEffect(() => {
    if (!open) return;
    setLoadingAvail(true);
    api.get("/onboarding/clinician-availability", { params: { date: form.visit_date || undefined, exclude: requestId } })
      .then((r) => { setNurses(r.data.nurses || []); setTechnicians(r.data.technicians || []); })
      .catch(() => {})
      .finally(() => setLoadingAvail(false));
  }, [open, form.visit_date, requestId]);

  useEffect(() => {
    if (!open) return;
    api.get("/lookups/categories", { params: { page_size: 200 } }).then(async (cats) => {
      const pri = cats.data.items.find((c) => c.code === "visit_priorities");
      if (pri) {
        const v = await api.get("/lookups/values", { params: { category_id: pri.id } });
        setPriorities(v.data.items);
      }
    }).catch(() => {});
  }, [open]);

  function toggle(key, id) {
    setForm((f) => {
      const arr = f[key];
      return { ...f, [key]: arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id] };
    });
  }

  const selectedBusy = [...nurses, ...technicians].filter(
    (p) => (form.nurse_ids.includes(p.id) || form.technician_ids.includes(p.id)) && (p.booked_count || 0) > 0
  );

  async function save() {
    if (!form.visit_date) { toast.error("Visit date is required."); return; }
    if (form.nurse_ids.length === 0) { toast.error("At least one nurse must be assigned."); return; }
    if (form.technician_ids.length === 0) { toast.error("At least one H@H technician must be assigned."); return; }
    setSaving(true);
    try {
      const res = await api.post(`/onboarding/requests/${requestId}/assign`, { ...form, override_availability: selectedBusy.length > 0 });
      toast.success("Home care team assigned");
      onSaved && onSaved(res.data);
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-[560px] overflow-y-auto p-0 bg-[#FAFBFC]">
        <SheetHeader className="px-6 py-5 border-b border-[#E5E7EB] bg-white">
          <SheetTitle>Assign Home Care Team</SheetTitle>
          <SheetDescription>Assign at least one nurse and one H@H technician. Availability for the selected day is shown — you can still override and assign.</SheetDescription>
        </SheetHeader>
        <div className="p-6 space-y-5">
          <FormSection title="Visit schedule" testId="ob-assign-form">
            <FormField label="Visit date" required>
              <Input type="date" data-testid="ob-assign-date" value={form.visit_date} onChange={(e) => setForm({ ...form, visit_date: e.target.value })} />
            </FormField>
            <FormField label="Visit time">
              <Input type="time" data-testid="ob-assign-time" value={form.visit_time} onChange={(e) => setForm({ ...form, visit_time: e.target.value })} />
            </FormField>
            <FormField label="Priority">
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger data-testid="ob-assign-priority"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {priorities.length > 0 ? priorities.map((p) => (
                    <SelectItem key={p.id} value={p.value}>{p.label}</SelectItem>
                  )) : (
                    <>
                      <SelectItem value="routine">Routine</SelectItem>
                      <SelectItem value="urgent">Urgent</SelectItem>
                      <SelectItem value="stat">STAT</SelectItem>
                    </>
                  )}
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="Notes" full>
              <Textarea rows={2} data-testid="ob-assign-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </FormField>
          </FormSection>

          <div className="hah-card p-4 space-y-4">
            <div className="flex items-center justify-between">
              <div className="hah-section-title">Care team {form.visit_date ? `· availability for ${form.visit_date}` : "· pick a date to see availability"}</div>
              {loadingAvail && <span className="text-[11px] text-[#9CA3AF]">Checking…</span>}
            </div>
            <ClinicianPicker title="Nurses (≥ 1 required)" people={nurses} selectedIds={form.nurse_ids} onToggle={(id) => toggle("nurse_ids", id)} testId="ob-assign-nurses" />
            <ClinicianPicker title="H@H Technicians (≥ 1 required)" people={technicians} selectedIds={form.technician_ids} onToggle={(id) => toggle("technician_ids", id)} testId="ob-assign-technicians" />
          </div>

          {selectedBusy.length > 0 && (
            <div data-testid="ob-assign-override-warning" className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              <div className="flex items-center gap-1.5 font-semibold mb-1"><ShieldAlert className="w-4 h-4" /> Double-booking warning</div>
              {selectedBusy.map((p) => <div key={p.id}>{p.name} already has {p.booked_count} visit(s) on this day.</div>)}
              <div className="mt-1 text-xs">You can still proceed — they'll be assigned anyway.</div>
            </div>
          )}
        </div>
        <StickyActionBar>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-[#E5E7EB]">Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid="ob-assign-save">
            {saving ? "Assigning…" : "Confirm assignment"}
          </Button>
        </StickyActionBar>
      </SheetContent>
    </Sheet>
  );
}


// --------------- Visit Checklist Sheet ---------------
const CHECKLIST_ITEMS = [
  ["patient_verification", "Patient verification"],
  ["address_verification", "Address verification"],
  ["home_env_verification", "Home environment verification"],
  ["connectivity_verification", "Connectivity verification"],
  ["caregiver_verification", "Companion verification"],
  ["rpm_readiness", "RPM readiness verification"],
  ["infection_control", "Infection control assessment"],
  ["waste_management", "Waste management assessment"],
];

function VisitChecklistSheet({ open, onOpenChange, requestId, initial, canComplete, onSaved }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(initial || {});
  }, [open, initial]);

  async function save(complete = false) {
    setSaving(true);
    try {
      const res = await api.post(`/onboarding/requests/${requestId}/save-checklist`, form);
      let result = res.data;
      if (complete) {
        const res2 = await api.post(`/onboarding/requests/${requestId}/complete-visit`);
        result = res2.data;
        toast.success("Visit completed");
      } else {
        toast.success("Checklist saved");
      }
      onSaved && onSaved(result);
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-[600px] overflow-y-auto p-0 bg-[#FAFBFC]">
        <SheetHeader className="px-6 py-5 border-b border-[#E5E7EB] bg-white">
          <SheetTitle>Home Visit Checklist</SheetTitle>
          <SheetDescription>Complete the checklist while on-site. Save progress at any time.</SheetDescription>
        </SheetHeader>
        <div className="p-6 space-y-4">
          <FormSection title="Verification" testId="ob-visit-verification">
            {CHECKLIST_ITEMS.map(([k, label]) => (
              <FormField key={k} label={label} full>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={form[k] === true}
                      onCheckedChange={() => setForm({ ...form, [k]: form[k] === true ? null : true })}
                      data-testid={`ob-visit-${k}-yes`}
                    />
                    Verified
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={form[k] === false}
                      onCheckedChange={() => setForm({ ...form, [k]: form[k] === false ? null : false })}
                      data-testid={`ob-visit-${k}-no`}
                    />
                    Not verified
                  </label>
                </div>
              </FormField>
            ))}
          </FormSection>
          <FormSection title="Notes & evidence" testId="ob-visit-notes">
            <FormField label="Additional findings" full>
              <Textarea rows={3} value={form.additional_findings || ""} onChange={(e) => setForm({ ...form, additional_findings: e.target.value })} data-testid="ob-visit-findings" />
            </FormField>
            <FormField label="Visit notes" full>
              <Textarea rows={3} value={form.visit_notes || ""} onChange={(e) => setForm({ ...form, visit_notes: e.target.value })} data-testid="ob-visit-notes-input" />
            </FormField>
          </FormSection>
          <div className="text-xs text-[#6B7280]">Upload photos and other evidence from the request detail "Attachments" tab.</div>
        </div>
        <StickyActionBar>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-[#E5E7EB]">Cancel</Button>
          <Button variant="outline" onClick={() => save(false)} disabled={saving} className="border-[#0B7E8C] text-[#0B7E8C] hover:bg-[#EAF8FA]" data-testid="ob-visit-save">
            Save progress
          </Button>
          {canComplete && (
            <Button onClick={() => save(true)} disabled={saving} className="bg-[#10B981] hover:bg-[#059669] text-white" data-testid="ob-visit-complete">
              <CheckCircle2 className="w-4 h-4 mr-1.5" /> Complete visit
            </Button>
          )}
        </StickyActionBar>
      </SheetContent>
    </Sheet>
  );
}
